import React, { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Badge, Btn, Input, Textarea, Select, Field, Modal } from "@/components/ui-kit";
import { PO_STATUSES, fmtMoney, fmtDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { ShoppingCart, Plus, ExternalLink, UploadCloud, FileText } from "lucide-react";

const STATUS_CLS = {
  draft: "bg-slate-500/15 text-slate-500 border-slate-500/30",
  ordered: "bg-blue-500/15 text-blue-500 border-blue-500/30",
  partially_received: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  received: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-500 border-red-500/30",
};

export default function PurchaseOrders() {
  const { can } = useAuth();
  const [pos, setPos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/purchase-orders").then((r) => setPos(r.data));
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, []);

  const canEdit = can("po.manage");
  const changeStatus = async (po, status) => {
    try { await api.put(`/purchase-orders/${po.id}`, { status }); toast.success(`${po.po_number} → ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="po-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl">Purchase Orders</h1>
          <p className="text-sm text-muted-foreground">Supplier tracking · landed cost · receipt uploads · {pos.length} POs</p>
        </div>
        {canEdit && <Btn onClick={() => setOpen(true)} data-testid="new-po-btn"><Plus size={16} /> New PO</Btn>}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {pos.map((po) => {
          const proj = projects.find((p) => p.id === po.project_id);
          return (
            <Card key={po.id} className="p-4" data-testid={`po-card-${po.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <ShoppingCart size={15} className="text-primary" />
                    <span className="font-mono text-sm font-bold text-primary">{po.po_number}</span>
                    <Badge className={STATUS_CLS[po.status] || "border-border"}>{po.status}</Badge>
                  </div>
                  <div className="mt-1 font-semibold">{po.supplier_name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{fmtDate(po.created_at)} · {po.ordered_by_name || "—"}</div>
                </div>
                <div className="text-right"><div className="font-mono text-lg font-bold">{fmtMoney(po.total_amount)}</div></div>
              </div>

              {po.line_items?.length > 0 && (
                <div className="mt-2 space-y-0.5 rounded-md border border-border p-2 text-xs">
                  {po.line_items.map((li, i) => <div key={i} className="flex justify-between"><span>{li.name} ×{li.qty}</span><span className="font-mono">{fmtMoney((li.unit || 0) * (li.qty || 0))}</span></div>)}
                </div>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {proj && <Badge className="border-border text-muted-foreground">{proj.project_code}</Badge>}
                {po.tracking_number && <span className="font-mono text-muted-foreground">AWB {po.tracking_number}</span>}
                {po.tracking_url && <a href={po.tracking_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline">Track <ExternalLink size={11} /></a>}
                {po.receipt_document_url && <a href={po.receipt_document_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-emerald-500 hover:underline"><FileText size={12} /> Receipt</a>}
              </div>

              {canEdit && (
                <div className="mt-3 flex items-center gap-2">
                  <Select value={po.status} onChange={(e) => changeStatus(po, e.target.value)} className="h-8 w-44 text-xs" data-testid={`po-status-${po.id}`}>
                    {PO_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </div>
              )}
            </Card>
          );
        })}
        {pos.length === 0 && <p className="text-sm text-muted-foreground">No purchase orders yet.</p>}
      </div>

      {open && <POForm projects={projects} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function POForm({ projects, onClose, onSaved }) {
  const [f, setF] = useState({ supplier_name: "", project_id: "", tracking_number: "", tracking_url: "", status: "draft", total_amount: 0, notes: "", receipt_document_url: "" });
  const [items, setItems] = useState([{ name: "", qty: 1, unit: 0 }]);
  const fileRef = useRef();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const total = items.reduce((s, i) => s + (Number(i.unit) || 0) * (Number(i.qty) || 0), 0);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3_000_000) { toast.error("File too large (max 3MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setF((p) => ({ ...p, receipt_document_url: reader.result }));
    reader.readAsDataURL(file);
    toast.success("Receipt attached");
  };
  const onDrop = (e) => { e.preventDefault(); const dt = e.dataTransfer.files?.[0]; if (dt) { fileRef.current.files = e.dataTransfer.files; onFile({ target: { files: e.dataTransfer.files } }); } };

  const save = async () => {
    if (!f.supplier_name) { toast.error("Supplier required"); return; }
    try {
      await api.post("/purchase-orders", { ...f, project_id: f.project_id || null, total_amount: total || Number(f.total_amount), line_items: items.filter((i) => i.name).map((i) => ({ name: i.name, qty: Number(i.qty), unit: Number(i.unit) })) });
      toast.success("PO created"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Modal open onClose={onClose} title="New Purchase Order" wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Supplier" required><Input data-testid="po-supplier" value={f.supplier_name} onChange={set("supplier_name")} placeholder="Mouser / DigiKey / Farnell / Local" /></Field>
        <Field label="Link to Project"><Select value={f.project_id} onChange={set("project_id")}><option value="">—</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.project_code}</option>)}</Select></Field>
        <Field label="Tracking Number"><Input value={f.tracking_number} onChange={set("tracking_number")} /></Field>
        <Field label="Tracking URL"><Input value={f.tracking_url} onChange={set("tracking_url")} placeholder="https://…" /></Field>
      </div>

      <div className="mt-3">
        <div className="mb-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Line Items</div>
        {items.map((it, idx) => (
          <div key={idx} className="mb-1.5 flex gap-2">
            <Input placeholder="Component" value={it.name} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} />
            <Input type="number" className="w-20" placeholder="Qty" value={it.qty} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, qty: e.target.value } : x))} />
            <Input type="number" className="w-28" placeholder="Unit ₹" value={it.unit} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, unit: e.target.value } : x))} />
          </div>
        ))}
        <button onClick={() => setItems([...items, { name: "", qty: 1, unit: 0 }])} className="text-xs text-primary hover:underline">+ Add line</button>
        <div className="mt-1 text-right font-mono text-sm font-bold">Total: {fmtMoney(total)}</div>
      </div>

      <div className="mt-3">
        <div className="mb-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Receipt / Invoice (drag & drop PDF/PNG/JPG)</div>
        <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop} onClick={() => fileRef.current.click()}
          data-testid="po-receipt-drop"
          className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-border py-5 text-sm text-muted-foreground hover:border-primary">
          <UploadCloud size={20} />
          {f.receipt_document_url ? <span className="text-emerald-500">Receipt attached ✓</span> : "Click or drop file here"}
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={onFile} />
        </div>
      </div>

      <Field label="Notes"><Textarea className="mt-3" rows={2} value={f.notes} onChange={set("notes")} /></Field>
      <div className="mt-4 flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="po-submit-btn">Create PO</Btn></div>
    </Modal>
  );
}
